import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import Navigation from './components/Navigation';
import LandingPage from './components/LandingPage';
import RoomsPage from './components/RoomsPage';
import DashboardsPage from './components/DashboardsPage';
import CustomDashboard from './components/CustomDashboard';
import LoginForm from './components/Auth/LoginForm';
import RegisterForm from './components/Auth/RegisterForm';
import EmailConfirmationSuccess from './components/Auth/EmailConfirmationSuccess';
import BroadcastView from './components/BroadcastView';
import SuccessPage from './components/SuccessPage';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';
import ApiKeyManagement from './components/ApiKeyManagement';
import Footer from './components/Footer';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

function App() {
  const { initialize, loading, initialized } = useAuthStore();
  const { initializeTheme } = useThemeStore();

  useEffect(() => {
    // Initialize theme first
    initializeTheme();

    if (!initialized) {
      console.log('App: Initializing auth...');
      initialize().catch((error) => {
        console.error('App: Auth initialization failed:', error);
      });
    }
  }, [initialize, initialized, initializeTheme]);

  // Show loading only while initializing
  if (!initialized && loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 mx-auto mb-4"></div>
            <div
              className="absolute inset-0 rounded-full h-12 w-12 border-4 border-transparent border-t-purple-400 dark:border-t-purple-600 animate-spin mx-auto"
              style={{
                animationDirection: 'reverse',
                animationDuration: '1.5s',
              }}
            ></div>
          </div>
          <p className="text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If not initialized but not loading, something went wrong - show login
  if (!initialized) {
    console.warn('App: Not initialized and not loading, redirecting to login');
  }

  return (
    <Router>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
        <Routes>
          {/* Public routes without navigation */}
          <Route path="/login" element={<LoginForm />} />
          <Route path="/register" element={<RegisterForm />} />
          <Route path="/auth/callback" element={<EmailConfirmationSuccess />} />
          <Route
            path="/broadcast/:roomId/:linkId"
            element={<BroadcastView />}
          />
          <Route path="/success" element={<SuccessPage />} />

          {/* Routes with navigation */}
          <Route
            path="/*"
            element={
              <div className="flex flex-col min-h-screen">
                <Navigation />
                <div className="flex-1">
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route
                      path="/rooms"
                      element={
                        <PrivateRoute>
                          <RoomsPage />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/dashboards"
                      element={
                        <PrivateRoute>
                          <DashboardsPage />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/custom-dashboard"
                      element={
                        <PrivateRoute>
                          <CustomDashboard />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/api-keys"
                      element={
                        <PrivateRoute>
                          <ApiKeyManagement />
                        </PrivateRoute>
                      }
                    />
                  </Routes>
                </div>
                <Footer />
              </div>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;